import {
  type DynamicClient,
  assertWalletAccountSigningAvailability,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  createWalletProviderEventEmitter,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { SuiClient } from '@mysten/sui/client';
import type {
  StandardEventsChangeProperties,
  SuiWalletFeatures,
  WalletWithFeatures,
} from '@mysten/wallet-standard';

import type { SuiWalletProvider } from '../../../SuiWalletProvider.types';
import { suiExecuteCheckoutTransaction } from '../../../utils/suiExecuteCheckoutTransaction';
import { suiTransferAmount } from '../../../utils/suiTransferAmount';

type CreateWalletProviderFromSuiStandardWalletParams = {
  dynamicClient: DynamicClient;
  walletStandardWallet: WalletWithFeatures<SuiWalletFeatures>;
};

export const createWalletProviderFromSuiStandardWallet = ({
  dynamicClient,
  walletStandardWallet,
}: CreateWalletProviderFromSuiStandardWalletParams): SuiWalletProvider => {
  const features = walletStandardWallet.features;

  const walletProviderType = WalletProviderEnum.BrowserExtension;

  const key = formatWalletProviderKey({
    chain: 'SUI',
    displayName: walletStandardWallet.name,
    walletProviderType,
  });

  const runConnectFeature = async ({
    silent = false,
  }: {
    silent?: boolean;
  } = {}) => {
    const connectMethod = features['standard:connect']?.connect;

    assertDefined(connectMethod, 'connect method not found in wallet features');

    const result = await connectMethod({ silent });

    return {
      addresses: result.accounts?.map((account) => ({
        address: account.address,
        publicKey: account.publicKey?.toString(),
      })),
    };
  };

  const connect: SuiWalletProvider['connect'] = async () => {
    const autoConnectedAccounts = walletStandardWallet.accounts;

    if (autoConnectedAccounts?.length && autoConnectedAccounts[0].address) {
      return {
        addresses: [
          {
            address: autoConnectedAccounts[0].address,
            publicKey: autoConnectedAccounts[0].publicKey?.toString(),
          },
        ],
      };
    }

    return runConnectFeature();
  };

  const disconnectFeature = features['standard:disconnect']?.disconnect;

  const getActiveNetworkId: SuiWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: dynamicClient,
        walletProviderKey: key,
      });

  const getCurrentAccount = async () => {
    if (!isConnected()) {
      await runConnectFeature({ silent: true });
    }

    const account = walletStandardWallet.accounts?.[0];

    assertDefined(account, 'No connected accounts found');

    return account;
  };

  const getSuiClient: SuiWalletProvider['getSuiClient'] = async ({
    walletAccount,
  }) => {
    const activeNetworkData = await getActiveNetworkData(
      { walletAccount },
      dynamicClient
    );
    assertDefined(activeNetworkData, 'No active network data found');

    const url = activeNetworkData.networkData?.rpcUrls.http[0];
    assertDefined(url, 'No RPC URL found for active network');

    const client = new SuiClient({ url });
    return client;
  };

  const isConnected = (): boolean => {
    return walletStandardWallet.accounts?.length > 0;
  };

  const getConnectedAddresses: SuiWalletProvider['getConnectedAddresses'] =
    async () => {
      if (!isConnected()) {
        await runConnectFeature({ silent: true });
      }

      return {
        addresses: walletStandardWallet.accounts.map(
          (account) => account.address
        ),
      };
    };

  const signAndExecuteTransaction: SuiWalletProvider['signAndExecuteTransaction'] =
    async ({ transaction, walletAccount }) => {
      await assertWalletAccountSigningAvailability(
        {
          walletAccount,
        },
        dynamicClient
      );

      const signAndExecuteTransactionMethod =
        features['sui:signAndExecuteTransaction']?.signAndExecuteTransaction;

      assertDefined(
        signAndExecuteTransactionMethod,
        'signAndExecuteTransaction method not found in wallet features'
      );

      const account = await getCurrentAccount();

      return signAndExecuteTransactionMethod({
        account,
        chain: account.chains[0],
        transaction,
      });
    };

  /* This method is deprecated, but still required by some wallets for backwards compatibility. */
  const signAndExecuteTransactionBlock: SuiWalletProvider['signAndExecuteTransactionBlock'] =
    async ({ options, requestType, transactionBlock, walletAccount }) => {
      await assertWalletAccountSigningAvailability(
        {
          walletAccount,
        },
        dynamicClient
      );

      const signAndExecuteTransactionBlockMethod =
        features['sui:signAndExecuteTransactionBlock']
          ?.signAndExecuteTransactionBlock;

      assertDefined(
        signAndExecuteTransactionBlockMethod,
        'signAndExecuteTransactionBlock method not found in wallet features'
      );

      const account = await getCurrentAccount();

      return signAndExecuteTransactionBlockMethod({
        account,
        chain: account.chains[0],
        options,
        requestType,
        transactionBlock,
      });
    };

  const signMessage: SuiWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    const signMessageMethod =
      features['sui:signPersonalMessage']?.signPersonalMessage;

    assertDefined(
      signMessageMethod,
      'signPersonalMessage method not found in wallet features'
    );

    assertDefined(walletAccount, 'Wallet account not found');

    await assertWalletAccountSigningAvailability(
      {
        walletAccount,
      },
      dynamicClient
    );

    const encodedMessage = new TextEncoder().encode(message);

    const account = await getCurrentAccount();

    const { signature } = await signMessageMethod({
      account,
      message: encodedMessage,
    });

    return { signature };
  };

  const signTransaction: SuiWalletProvider['signTransaction'] = async ({
    transaction,
    walletAccount,
  }) => {
    const signTransactionMethod =
      features['sui:signTransaction']?.signTransaction;

    assertDefined(
      signTransactionMethod,
      'signTransaction method not found in wallet features'
    );

    await assertWalletAccountSigningAvailability(
      { walletAccount },
      dynamicClient
    );

    const account = await getCurrentAccount();

    return signTransactionMethod({
      account,
      chain: account.chains[0],
      transaction,
    });
  };

  /* This method is deprecated, but still required by some wallets for backwards compatibility. */
  const signTransactionBlock: SuiWalletProvider['signTransactionBlock'] =
    async ({ transactionBlock, walletAccount }) => {
      const signTransactionBlockMethod =
        features['sui:signTransactionBlock']?.signTransactionBlock;

      assertDefined(
        signTransactionBlockMethod,
        'signTransactionBlock method not found in wallet features'
      );

      await assertWalletAccountSigningAvailability(
        { walletAccount },
        dynamicClient
      );

      const account = await getCurrentAccount();

      return signTransactionBlockMethod({
        account,
        chain: account.chains[0],
        transactionBlock,
      });
    };

  const switchActiveNetwork: SuiWalletProvider['switchActiveNetwork'] = async ({
    networkId,
  }) =>
    switchActiveNetworkInLastKnownRegistry({
      client: dynamicClient,
      networkId,
      walletProviderKey: key,
    });

  const { getEventEmitter, cleanupEventEmitter } =
    createWalletProviderEventEmitter<VoidFunction>({
      removeEventListeners: ({ setupReturnValue }) => {
        assertDefined(setupReturnValue, 'Setup return value not defined');

        setupReturnValue();
      },

      setupEventListeners: ({ handleAccountsChanged }) => {
        const eventsFeature = features['standard:events'];

        return eventsFeature.on(
          'change',
          ({ accounts }: StandardEventsChangeProperties) => {
            handleAccountsChanged({
              addresses: accounts?.map((account) => account.address) ?? [],
            });
          }
        );
      },

      supportedEvents: ['accountsChanged'],
    });

  const terminate: SuiWalletProvider['terminate'] = async () => {
    cleanupEventEmitter();
  };

  const walletProvider: SuiWalletProvider = {
    chain: 'SUI',
    connect,
    disconnect: disconnectFeature,
    get events() {
      return getEventEmitter();
    },
    executeCheckoutTransaction: (args) =>
      suiExecuteCheckoutTransaction(args, dynamicClient),
    getActiveNetworkId,
    getConnectedAddresses,
    getSuiClient,
    groupKey: formatWalletProviderGroupKey(walletStandardWallet.name),
    key,
    metadata: {
      displayName: walletStandardWallet.name,
      icon: walletStandardWallet.icon,
    },
    signAndExecuteTransaction,
    signAndExecuteTransactionBlock,
    signMessage,
    signTransaction,
    signTransactionBlock,
    switchActiveNetwork,
    terminate,
    transferAmount: (args) => suiTransferAmount(args, dynamicClient),
    walletProviderType,
  };

  return walletProvider;
};
